import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import FeatureBranch from '@/common/FeatureBranch';
import HideOnNative from '@/common/HideOnNative';
import PageFooter from '@/common/PageFooter';
import BelowFoldViewHandler from '@/vdp/Analytics/BelowFoldViewHandler';
import BreadCrumbs from '@/vdp/BreadCrumbs';
import DeliveryUnavailableBanner from '@/vdp/DeliveryUnavailableBanner';
import dynamicLoader from '@/vdp/dynamicLoader';
import GalleryAndDelivery from '@/vdp/GalleryAndDelivery';
import Hero from '@/vdp/Hero';
import KeyFeatures from '@/vdp/KeyFeatures';
import PageHeader from '@/vdp/PageHeader';
import ShowForInventoryType from '@/vdp/ShowForInventoryType';
import SocialProof from '@/vdp/SocialProof';
import VehiclesYouMightLikeLoader from '@/vdp/VehiclesYouMightLike/VehiclesYouMightLikeLoader';
import { InventoryTypeKey } from '~/constants/inventoryType';
import Sections from '~/constants/sections';
import { useDeviceType } from '~/context/common/DeviceContext';
import { useIsInExperiment } from '~/hooks/common';
import { useShowPriceDetailsImmediately } from '~/hooks/vdp';
import NativeError from '~/native/NativeError';
import {
  getHasOwnerReviews,
  getIsDealerMarketplace,
  getIsNotAvailableForImmediatePurchase,
  getShouldLoadBelowFirstSectionStandard,
  getShouldLoadFirstSection,
  getShouldRenderBelowSpinner
} from '~/selectors';
import { useShouldImport } from './hooks';
import SectionsWrapper from './SectionsWrapper';
import { Wrapper } from './VehicleDetailsContainer.styles';

const ReservableHeader = dynamic(
  () =>
    import(
      /* webpackChunkName: "ReservableHeader" */ '@/vdp/Reservable/ReservableHeader'
    )
);

const DesktopPageNavigation = dynamic(
  () =>
    import(
      /* webpackChunkName: "DesktopPageNavigation" */ '@/vdp/DesktopPageNavigation'
    )
);

const StickyBanner = dynamic(
  () => import(/* webpackChunkName: "StickyBanner" */ '@/vdp/StickyBanner'),
  { ssr: false }
);

const VehicleDetails = dynamic(
  () => import(/* webpackChunkName: "VehicleDetails" */ '@/vdp/VehicleDetails'),
  {
    ssr: false,
    loading: dynamicLoader(Sections.VehicleDetails)
  }
);

const OwnerReviews = dynamic(
  () => import(/* webpackChunkName: "OwnerReviews" */ '@/vdp/OwnerReviews'),
  {
    ssr: false,
    loading: dynamicLoader(Sections.OwnerReviews)
  }
);

const One50PointInspection = dynamic(
  () => import(/* webpackChunkName: "150PointInspection" */ '@/vdp/Inspection'),
  { ssr: false, loading: dynamicLoader(Sections.Inspection150Point) }
);

const VehiclesYouMightLike = dynamic(
  () =>
    import(
      /* webpackChunkName: "VehiclesYouMightLike" */ '@/vdp/VehiclesYouMightLike'
    ),
  {
    ssr: false,
    loading: dynamicLoader(Sections.VehiclesYouMightLike, () => (
      <VehiclesYouMightLikeLoader />
    ))
  }
);

const Disclaimer = dynamic(
  () => import(/* webpackChunkName: "Disclaimer" */ '@/vdp/Disclaimer'),
  {
    ssr: false,
    loading: dynamicLoader(Sections.Disclaimer)
  }
);

const StickyCta = dynamic(
  () => import(/* webpackChunkName: "StickyCta" */ '@/vdp/StickyCta'),
  { ssr: false }
);

const PriceDetails = dynamic(
  () => import(/* webpackChunkName: "PriceDetails" */ '@/vdp/PriceDetails'),
  {
    ssr: false,
    loading: dynamicLoader(Sections.PriceDetails)
  }
);

const Modals = dynamic(
  () => import(/* webpackChunkName: "Modals" */ './Modals'),
  { ssr: false }
);

const AncillaryProducts = dynamic(
  () =>
    import(
      /* webpackChunkName: "AncillaryProducts" */ 'src/components/vdp/AncillaryProducts'
    ),
  { ssr: false }
);

const BuyWithConfidence = dynamic(
  () =>
    import(
      /* webpackChunkName: "BuyWithConfidence" */ '@/vdp/BuyWithConfidence'
    ),
  { ssr: false }
);

const ShowcasePortal = dynamic(
  () =>
    import(
      /* webpackChunkName: "ShowcasePortal" */ '@/vdp/Showcase/ShowcasePortal'
    ),
  { ssr: false }
);

const VehicleDetailsStandardContainer = () => {
  const hasOwnerReviews = useSelector(getHasOwnerReviews);
  const isMarketplace = useSelector(getIsDealerMarketplace);
  const prioritizeMCYML = useSelector(getIsNotAvailableForImmediatePurchase);
  const { isDesktop, isMobile } = useDeviceType();
  const shouldLoadFirstSection = useSelector(getShouldLoadFirstSection);
  const shouldLoadBelowFirstSection = useSelector(
    getShouldLoadBelowFirstSectionStandard
  );
  const renderBelowSpinner = useSelector(getShouldRenderBelowSpinner);
  const isInStickyNavExperiment = useIsInExperiment(
    'vdp-sticky-nav',
    'experiment'
  );
  const { importReservableHeader } = useShouldImport();
  const showPriceDetailsImmediately = useShowPriceDetailsImmediately();

  return (
    <>
      <PageHeader />
      <FeatureBranch appName="merchui" />
      <NativeError />
      <Wrapper id={Sections.Root} isMarketplace={isMarketplace}>
        <ShowForInventoryType
          inventoryType={[
            InventoryTypeKey.Core,
            InventoryTypeKey.DealerMarketplace
          ]}
        >
          <BreadCrumbs />
        </ShowForInventoryType>
        <DeliveryUnavailableBanner />
        <SectionsWrapper>
          <HideOnNative>
            <StickyBanner />
          </HideOnNative>
          {importReservableHeader && <ReservableHeader />}
          <ShowForInventoryType inventoryType={[InventoryTypeKey.WIP]}>
            <BreadCrumbs />
          </ShowForInventoryType>
          <Hero />
          {isDesktop && isInStickyNavExperiment && <DesktopPageNavigation />}
          <SocialProof />
          <GalleryAndDelivery />
          {renderBelowSpinner && prioritizeMCYML && <VehiclesYouMightLike />}

          <KeyFeatures />

          <BelowFoldViewHandler
            id={Sections.VehicleDetails}
            key={Sections.VehicleDetails}
            className={shouldLoadFirstSection ? undefined : 'will-load'}
          >
            {shouldLoadFirstSection && <VehicleDetails />}
          </BelowFoldViewHandler>

          <BelowFoldViewHandler
            id={Sections.PriceDetails}
            key={Sections.PriceDetails}
            className={shouldLoadBelowFirstSection ? undefined : 'will-load'}
          >
            {(shouldLoadBelowFirstSection || showPriceDetailsImmediately) && (
              <PriceDetails />
            )}
          </BelowFoldViewHandler>

          <BelowFoldViewHandler
            id={Sections.OwnerReviews}
            key={Sections.OwnerReviews}
            className={shouldLoadBelowFirstSection ? undefined : 'will-load'}
          >
            {shouldLoadBelowFirstSection && hasOwnerReviews && <OwnerReviews />}
          </BelowFoldViewHandler>

          <BelowFoldViewHandler
            id={Sections.Inspection150Point}
            key={Sections.Inspection150Point}
            className={shouldLoadBelowFirstSection ? undefined : 'will-load'}
          >
            {shouldLoadBelowFirstSection && <One50PointInspection />}
          </BelowFoldViewHandler>

          <BelowFoldViewHandler
            id={Sections.BuyWithConfidence}
            key={Sections.BuyWithConfidence}
            className={shouldLoadBelowFirstSection ? undefined : 'will-load'}
          >
            {shouldLoadBelowFirstSection && <BuyWithConfidence />}
          </BelowFoldViewHandler>

          <BelowFoldViewHandler
            id={Sections.AncillaryProducts}
            key={Sections.AncillaryProducts}
            className={shouldLoadBelowFirstSection ? undefined : 'will-load'}
          >
            {shouldLoadBelowFirstSection && <AncillaryProducts />}
          </BelowFoldViewHandler>

          {shouldLoadBelowFirstSection && !prioritizeMCYML && (
            <VehiclesYouMightLike />
          )}
        </SectionsWrapper>

        {shouldLoadBelowFirstSection && <Disclaimer />}
        {renderBelowSpinner && isMobile && <StickyCta />}
      </Wrapper>
      <PageFooter />
      <Modals />
      <ShowcasePortal />
    </>
  );
};

export default VehicleDetailsStandardContainer;
